import React, { useEffect, useRef, useState } from 'react';
import { AnalysisArtifacts } from '../../types/conversation';

interface ArtifactDownloadStatusProps {
  analysisArtifacts?: AnalysisArtifacts | null;
  isGenerating?: boolean;
}

const PROGRESS_STAGES = [
  { pct: 10, label: 'Processando análise...' },
  { pct: 30, label: 'Organizando dados...' },
  { pct: 55, label: 'Gerando planilha XLSX...' },
  { pct: 70, label: 'Gerando PDF...' },
  { pct: 82, label: 'Gerando DOCX...' },
  { pct: 92, label: 'Gerando PPTX...' },
  { pct: 96, label: 'Finalizando arquivos...' },
  { pct: 100, label: 'Arquivos prontos!' },
];

export const ArtifactDownloadStatus: React.FC<ArtifactDownloadStatusProps> = ({
  analysisArtifacts,
  isGenerating = false,
}) => {
  const hasXlsx = Boolean(
    analysisArtifacts?.xlsx_download_url ||
      analysisArtifacts?.xlsx_base64 ||
      analysisArtifacts?.xlsx_artifact_id
  );
  const hasPdf = Boolean(
    analysisArtifacts?.pdf_download_url ||
      analysisArtifacts?.pdf_base64 ||
      analysisArtifacts?.pdf_artifact_id
  );
  const hasDocx = Boolean(
    analysisArtifacts?.docx_download_url ||
      analysisArtifacts?.docx_base64 ||
      analysisArtifacts?.docx_artifact_id
  );
  const hasPptx = Boolean(
    analysisArtifacts?.pptx_download_url ||
      analysisArtifacts?.pptx_base64 ||
      analysisArtifacts?.pptx_artifact_id
  );
  const allReady = hasXlsx && hasPdf && hasDocx && hasPptx;

  const [progress, setProgress] = useState(0);
  const [stageLabel, setStageLabel] = useState(PROGRESS_STAGES[0].label);
  const [visible, setVisible] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stageIndexRef = useRef(0);

  useEffect(() => {
    if (!isGenerating && !(!allReady && analysisArtifacts !== undefined)) {
      return;
    }
    setVisible(true);
    setFadeOut(false);
    stageIndexRef.current = 0;
    setProgress(PROGRESS_STAGES[0].pct);
    setStageLabel(PROGRESS_STAGES[0].label);

    intervalRef.current = setInterval(() => {
      const nextIdx = stageIndexRef.current + 1;
      if (nextIdx < PROGRESS_STAGES.length - 1) {
        stageIndexRef.current = nextIdx;
        setProgress(PROGRESS_STAGES[nextIdx].pct);
        setStageLabel(PROGRESS_STAGES[nextIdx].label);
      }
    }, 1800);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [allReady, analysisArtifacts, isGenerating]);

  useEffect(() => {
    if (allReady && visible) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setProgress(100);
      setStageLabel(PROGRESS_STAGES[PROGRESS_STAGES.length - 1].label);
      setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => setVisible(false), 500);
      }, 1200);
    }
  }, [allReady, visible]);

  if (!visible) return null;

  const isDone = progress === 100;

  return (
    <div
      className={`transition-opacity duration-500 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
      style={{ marginTop: 12 }}
    >
      <div
        style={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          padding: '14px 18px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 10,
          }}
        >
          {isDone ? (
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: '#FF6B35',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2 6l3 3 5-5"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          ) : (
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                border: '2px solid #FF6B35',
                borderTopColor: 'transparent',
                display: 'inline-block',
                animation: 'devspott-spin 0.75s linear infinite',
              }}
            />
          )}
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: isDone ? '#FF6B35' : '#2C3E50',
            }}
          >
            {stageLabel}
          </span>
        </div>

        {/* Progress bar */}
        <div
          style={{
            height: 6,
            background: '#e2e8f0',
            borderRadius: 99,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: isDone
                ? 'linear-gradient(90deg, #FF6B35 0%, #ff9e6d 100%)'
                : 'linear-gradient(90deg, #1B3A57 0%, #FF6B35 100%)',
              borderRadius: 99,
              transition: 'width 0.9s cubic-bezier(0.4,0,0.2,1)',
            }}
          />
        </div>

        {/* File status chips */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Chip ready={hasXlsx} label="Planilha XLSX" icon="📊" />
          <Chip ready={hasPdf} label="PDF" icon="📄" />
          <Chip ready={hasDocx} label="DOCX" icon="🧾" />
          <Chip ready={hasPptx} label="PPTX" icon="📕" />
        </div>
      </div>

      {/* Spinner keyframes — injected once */}
      <style>{`
        @keyframes devspott-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

const Chip: React.FC<{ ready: boolean; label: string; icon: string }> = ({
  ready,
  label,
  icon,
}) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      padding: '3px 10px',
      borderRadius: 99,
      fontSize: 11,
      fontWeight: 600,
      background: ready ? '#fff4ef' : '#f1f5f9',
      color: ready ? '#FF6B35' : '#94a3b8',
      border: `1px solid ${ready ? '#FF6B35' : '#e2e8f0'}`,
      transition: 'all 0.4s ease',
    }}
  >
    {icon} {label} {ready ? '✓' : '...'}
  </span>
);
